// Shared lanterns (README "Together"): a glowing paper lantern rises through the sky of a city for everyone there.
// Its pose is a pure function of what the worker sent ({ x, at, s }) and the worker's clock, so every page shows it at
// the same place at the same moment, and a newcomer sees the ones already in flight. Drawn into the page's overlay
// buffers, never into a scene (PNG, MP4 and the mini player stay clean). Poses are tested in tests/together.test.mjs.
import { LANTERN_LIFE_MS, LANTERN_CITY_MAX, LANTERN_EVERY_MS, LANTERN_X_MIN, LANTERN_X_MAX } from './live.js';
import { W, TAU, clamp, smooth, hash, makeHalo, drawHalo } from '../engine/core.js';

const LIFE = LANTERN_LIFE_MS / 1000;
export const FADE_IN = 1, FADE_OUT = 6;      // seconds: lit at release, burning out over the last stretch
// Released mid-screen, at the same scene row on every page (the path can't depend on a page's layout): its sparkle
// (down to row 164) and glow (176) stay above the player and the city row. Measured in headless Chrome, the dock's top
// edge is at scene row 211 at 1280×720 (232 at 1440×900, 230 at 1920×1080), and on phones held upright at 191 (360×740)
// or 201 (390×844) for a screen-tall crop; a band (Hamburg, Istanbul…) ends higher up the scene, so from row 235 down.
export const START_Y = 150;
const TOP_Y = 30;                            // heading for the top, each one levelling off a little lower
export const SPARK = 1.4;                    // seconds of sparkle where it's lit: everyone's, so a newcomer is too late for it
const SPARKS = 12;
// Size steps as it recedes (seconds into the flight): 13×16 near, then 11×13, 9×11, 7×8, and 5×7 only as it burns
// out, so it stays easy to spot for most of its flight.
const STEPS = [18, 36, 54, 71];

// r rim, b paper, l the paper lit from inside, f the flame at the opening
// (flat-topped, tapering to the ring at the opening, lit from the flame up)
export const ART = [
  ['.rrrrrrrrrrr.', 'rrbbbbbbbbbrr', 'rbbbbbbbbbbbr', 'bbbbbbbbbbbbb', 'bbbbbbbbbbbbb', 'bbbbbbbbbbbbb', 'bbbbbbbbbbbbb',
    'bbbbbblbbbbbb', '.bbbblllbbbb.', '.bbblllllbbb.', '.bblllllllbb.', '.blllllllllb.', '..lllllllll..', '..lllllllll..',
    '...rlllllr...', '.....lfl.....'],
  ['.rrrrrrrrr.', 'rrbbbbbbbrr', 'rbbbbbbbbbr', 'bbbbbbbbbbb', 'bbbbbbbbbbb', 'bbbbblbbbbb', '.bbblllbbb.', '.bblllllbb.',
    '.blllllllb.', '..lllllll..', '..lllllll..', '...rlllr...', '....lfl....'],
  ['.rrrrrrr.', 'rrbbbbbrr', 'rbbbbbbbr', 'bbbbbbbbb', 'bbbbbbbbb', '.bbblbbb.', '.bblllbb.', '.blllllb.', '..lllll..',
    '..rlllr..', '...lfl...'],
  ['.rrrrr.', 'rbbbbbr', 'bbbbbbb', 'bbbbbbb', '.bblbb.', '.blllb.', '.lllll.', '..lfl..'],
  ['.rrr.', 'rbbbr', 'bbbbb', 'bblbb', '.lll.', '.lll.', '..f..'],
];
// halos per size: a wide one in the lit paper's colour that makes it easy to spot, and a hotter core in the flame's
const HALO = [[30, 13], [26, 11], [22, 9], [18, 8], [15, 6]];
// warm paper colours, picked by the lantern's seed: [rim, paper, lit, flame]
export const PALETTE = [
  ['#8a3b12', '#e0782a', '#ffb54a', '#fff2b0'],   // amber
  ['#7a1f1f', '#d8452e', '#ff8a4a', '#ffe9a8'],   // red
  ['#8c5a14', '#e8a93a', '#ffd873', '#fffbd6'],   // gold
  ['#7d2940', '#e0605a', '#ff9f7a', '#fff0c8'],   // rose
];
const NONE = Object.freeze([]);

/** A lantern from the worker as { x, at, s }, or null for junk. */
export function lanternOf(l) {
  if (!l || !Number.isFinite(l.x) || !Number.isFinite(l.at) || !Number.isFinite(l.s)) return null;
  return { x: clamp(Math.round(l.x), LANTERN_X_MIN, LANTERN_X_MAX), at: l.at, s: l.s >>> 0 };
}

/** The list with lantern `l` added (once), without the ones that burnt out by `now` (server ms), at most a city's worth. */
export function addLantern(list, l, now) {
  const add = lanternOf(l), kept = list.filter(o => now - o.at < LANTERN_LIFE_MS && !(add && o.at === add.at && o.s === add.s));
  if (add && now - add.at < LANTERN_LIFE_MS) kept.push(add);
  return kept.sort((a, b) => a.at - b.at).slice(-LANTERN_CITY_MAX);
}

/** where lantern `l` was lit: the middle of its paper at release (its sparkle bursts from there) */
const litAt = l => ({ x: Math.round(clamp(l.x + sway(l.s | 0, 0, 0), 2, W - 3)), y: START_Y - (ART[0].length >> 1) });
const sway = (k, age, u) => Math.sin(TAU * (age / (5 + 3 * hash(k, 3)) + hash(k, 4))) * 3 * (1 - 0.7 * u);

/**
 * The sparkle burst where lantern `l` was lit, `age` seconds into its flight: [{ x, y, a, big }] (whole pixels; big:
 * a 3×3 glint, else one pixel), empty outside its first SPARK seconds. Twelve sparks fly out and slow down, a little
 * upwards, each twinkling and going out at its own moment.
 */
export function sparkle(l, age) {
  if (!(age >= 0) || age >= SPARK) return NONE;
  const k = l.s | 0, u = age / SPARK, at = litAt(l), out = [];
  for (let i = 0; i < SPARKS; i++) {
    const a = TAU * (i + 0.8 * hash(k, 20 + i)) / SPARKS, reach = (13 + 13 * hash(k, 40 + i)) * (1 - (1 - u) ** 3);
    const end = 0.65 + 0.35 * hash(k, 60 + i);                        // when this one has gone out (share of SPARK)
    const a1 = 1 - smooth(end - 0.35, end, u);
    const blink = hash(k, 80 + i, Math.floor(age * 14)) < 0.18 ? 0.35 : 1;   // a twinkle, the same frame for everyone
    if (a1 <= 0) continue;
    out.push({
      x: Math.round(at.x + Math.cos(a) * reach), y: Math.round(at.y + Math.sin(a) * reach * 0.8 - 5 * u),
      a: a1 * blink, big: u < 0.65 && i % 3 !== 2,                   // warm glints (a rain drop is a dot), embers at the end
    });
  }
  return out;
}

/**
 * Where lantern `l` is `age` seconds into its flight, with the scene's landmark at `focusX`: { x, y, size, alpha, glow,
 * colour, flash, sparks } (x, y = the flame, whole pixels; size = the step in ART; colour = the PALETTE entry; flash:
 * the light where it was lit, 0 after SPARK; sparks: sparkle()), or null outside [0, LANTERN_LIFE_MS). It slows as it
 * recedes, sways less the smaller it gets, drifts a little towards the landmark (so a portrait crop keeps more of
 * them) and flickers.
 */
export function lanternPose(l, age, focusX = W / 2) {
  if (!(age >= 0) || age >= LIFE) return null;
  const u = age / LIFE, far = 1 - (1 - u) ** 2;
  const k = l.s | 0, top = TOP_Y + hash(k, 1) * 30;
  const wind = (hash(k, 2) - 0.5) * 28 * far;                            // a breeze of its own
  const x = l.x + (focusX - l.x) * 0.3 * far + wind + sway(k, age, u);
  const alpha = smooth(0, FADE_IN, age) * (1 - smooth(LIFE - FADE_OUT, LIFE, age));
  const flicker = 0.84 + 0.1 * Math.sin(age * 7.3 + hash(k, 5) * 40) + 0.06 * Math.sin(age * 17.9 + hash(k, 6) * 40);
  const step = STEPS.findIndex(s => age < s);
  return {
    x: Math.round(clamp(x, 2, W - 3)),
    y: Math.round(START_Y - (START_Y - top) * far),
    size: step < 0 ? STEPS.length : step,
    alpha,
    glow: alpha * flicker,
    colour: Math.floor(hash(k, 7) * PALETTE.length),
    flash: age < SPARK ? (1 - age / SPARK) ** 2 : 0,
    sparks: sparkle(l, age),
  };
}

/** ms until this page may release another lantern, after its last one at `last` (local ms, or null for never). */
export const lanternWait = (last, now) => (last == null ? 0 : Math.max(0, last + LANTERN_EVERY_MS - now));

/** ms until the city's sky has room for another lantern (0: room now), by `now` (server ms). */
export function roomIn(list, now) {
  const flying = list.filter(l => now - l.at < LANTERN_LIFE_MS);
  if (flying.length < LANTERN_CITY_MAX) return 0;
  return Math.max(0, Math.min(...flying.map(l => l.at)) + LANTERN_LIFE_MS - now);
}

/** A starting column inside what this page shows of the scene (`view` from display.view), clear of its edges. */
export function releaseX(view, rnd = Math.random) {
  const x0 = Math.max(view.x + 16, LANTERN_X_MIN), x1 = Math.min(view.x + view.w - 16, LANTERN_X_MAX);
  return Math.round(x1 > x0 ? x0 + rnd() * (x1 - x0) : clamp(view.x + view.w / 2, LANTERN_X_MIN, LANTERN_X_MAX));
}

let art = null;                     // [colour] → { sizes: [{ sprite, halo, core }], flash, glint, dot }, drawn on first use
function paint(rows, pal) {
  const c = document.createElement('canvas');
  c.width = rows[0].length; c.height = rows.length;
  const x = c.getContext('2d');
  rows.forEach((row, j) => [...row].forEach((ch, i) => {
    const col = pal['rblf'.indexOf(ch)];
    if (!col) return;
    x.fillStyle = col;
    x.fillRect(i, j, 1, 1);
  }));
  return c;
}
function build() {
  return PALETTE.map(pal => ({
    sizes: ART.map((rows, size) => ({
      sprite: paint(rows, pal), halo: makeHalo(HALO[size][0], pal[2], 0.5), core: makeHalo(HALO[size][1], pal[3], 0.6, 1.6),
    })),
    flash: makeHalo(34, pal[3], 0.7, 1.4),
    glint: paint(['.l.', 'lfl', '.l.'], pal),    // a spark while it's bright, then a dot
    dot: paint(['f'], pal),
  }));
}

/**
 * Draws the lanterns in flight at `now` (server ms): halos into `glow` (put on screen additively), paper, flame and
 * sparkle into `ink`. Drops the ones that burnt out from `list`; returns how many it drew.
 */
export function drawLanterns(glow, ink, list, now, focusX) {
  let drawn = 0, keep = 0;
  for (const l of list) {
    const age = (now - l.at) / 1000;
    if (age >= LIFE) continue;
    list[keep++] = l;
    const p = lanternPose(l, age, focusX);
    if (!p || (p.alpha <= 0 && !p.flash)) continue;
    art ??= build();
    const a = art[p.colour], { sprite, halo, core } = a.sizes[p.size], mid = p.y - (sprite.height >> 1);
    drawHalo(glow, halo, p.x, mid, p.glow);
    drawHalo(glow, core, p.x, mid, p.glow);
    if (p.flash) {
      const at = litAt(l);
      drawHalo(glow, a.flash, at.x, at.y, p.flash);
    }
    ink.globalAlpha = p.alpha;
    ink.drawImage(sprite, p.x - (sprite.width >> 1), p.y - sprite.height + 1);
    for (const s of p.sparks) {
      ink.globalAlpha = s.a;
      if (s.big) ink.drawImage(a.glint, s.x - 1, s.y - 1);
      else ink.drawImage(a.dot, s.x, s.y);
    }
    drawn++;
  }
  list.length = keep;
  ink.globalAlpha = 1;
  return drawn;
}
